import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { Microservices } from '../constants/pol.enums';
import { IProvider, IProviderPreferenceResponse, ISuggestiveSearchResponse } from '../models/state.model';
import { sortProviderFavorites } from '../../utils/sortProviderFavorites';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { ICareTypeResponse, IMemberNetworkResponse, IProviderDetailsRequest, IProviderDetailsResponse } from 'src/app/features/member/member-details/state/member-details.model';
import { IServiceTypes } from 'src/app/features/clinical/create-authorizations/create-authorizations.model';
import { Toast } from '../decorators/toast.decorator';

@Injectable({
  providedIn: 'root'
})
export class ProviderService {
  public static readonly CareTypeNetworkIdsKey = 'careTypeNetworkIds';
  public static getNetworkIds(): string[] {
    return JSON.parse(sessionStorage.getItem(ProviderService.CareTypeNetworkIdsKey)) as string[];
  }

  constructor(private http: HttpClient, private auth: OidcClientService) { }

  searchProvider(searchValue: string, vcodes: string[] = []): Observable<ISuggestiveSearchResponse> {
    return this.getCareTypeNetworkIds().pipe(
      mergeMap((res) => {
        const payload = {
          searchText: searchValue,
          resultSize: 50, // UI set as fixed value based on business requirement
          networkIds: res,
          billingProviderIdsFilter: vcodes
        };
        const apiUrl = `${Microservices.Provider}/Providers/Search/Suggestive`;
        return this.http.post<ISuggestiveSearchResponse>(apiUrl, payload);
      })
    );
  }

  @Toast({
    error: {
      title: 'Provider Search',
      message: 'The Provider Search request has not been successfully submitted.  Please try again.',
      disableTimeOut: true,
      closeButton: true
    }
  })
  suggestiveSearchProvider(payload): Observable<ISuggestiveSearchResponse> {
    const apiUrl = `${Microservices.ProviderOnline}/Authorizations/Providers/Search/Suggestive`;
    return this.getCareTypeNetworkIds().pipe(
      mergeMap((res) => {
        payload.networkIds = res;
        return this.http.post<ISuggestiveSearchResponse>(apiUrl, payload);
      })
    );
  }

  getServiceTypes(): Observable<IServiceTypes> {
    return this.http.get<IServiceTypes>(`${Microservices.ProviderOnline}/ServiceTypes`)
      .pipe(
        catchError(() => of({ serviceTypes: [] }))
      );
  }

  addFavoriteProvider(payload: IProvider): Observable<IProviderPreferenceResponse> {
    const apiUrl = `${Microservices.ProviderOnline}/UserPreferences/providerfavorites/Add`;
    return this.http.post<IProviderPreferenceResponse>(apiUrl, payload).pipe(
      map((response: IProviderPreferenceResponse) => {
        response.results = sortProviderFavorites(response.results);
        return response;
      })
    );
  }

  removeFavoriteProvider(payload: IProvider): Observable<IProviderPreferenceResponse> {
    const apiUrl = `${Microservices.ProviderOnline}/UserPreferences/providerfavorites/Delete`;
    return this.http
      .delete<IProviderPreferenceResponse>(apiUrl, {
        body: payload
      })
      .pipe(
        map((response: IProviderPreferenceResponse) => {
          response.results = sortProviderFavorites(response.results);
          return response;
        })
      );
  }

  getFavoriteProviders(): Observable<IProviderPreferenceResponse> {
    const apiUrl = `${Microservices.ProviderOnline}/UserPreferences/ProviderFavorites`;
    return this.http.get<IProviderPreferenceResponse>(apiUrl).pipe(
      map((res) => {
        res.results = sortProviderFavorites(res.results);
        return res;
      })
    );
  }

  getMemberNetworks(memberNumber: string): Observable<{ status: number; body: IMemberNetworkResponse }> {
    if (!memberNumber) {
      return of(null);
    }

    const user = this.auth.getUser().profile;
    const tpa = `${user.tpa_client}${user.tpa_id}`;
    const apiUrl = `${Microservices.ProviderDirectory}/MemberNetworks?tpaClientIdentifier=${tpa}&memberNumber=${memberNumber}`;
    return this.http.get<IMemberNetworkResponse>(apiUrl, { observe: 'response' }).pipe(
      map((res) => {
        return { status: res.status, body: res.body };
      })
    );
  }

  getProviderDetails(payload: IProviderDetailsRequest): Observable<IProviderDetailsResponse> {
    const apiUrl = `${Microservices.ProviderOnline}/ProviderNetworks`;
    return this.http.post<IProviderDetailsResponse>(apiUrl, payload);
  }

  getCareTypeNetworkIds(): Observable<string[]> {
    // check cache before sending network request
    const cache = ProviderService.getNetworkIds();

    if (cache) {
      return of(cache);
    }

    const user = this.auth.getUser().profile;
    const tpa = `${user.tpa_client}${user.tpa_id}`;
    const apiUrl = `${Microservices.ProviderDirectory}/v2/CareTypes/${tpa}?shouldShowAllNetworks=true`;
    return this.http.get<ICareTypeResponse>(apiUrl).pipe(
      map((res) => {
        // we only care about Medical, Behavioral Health, and Home & Community-Based Services
        res.careTypes = res.careTypes.filter((i) => {
          return ['Medical', 'Behavioral Health', 'Home & Community-Based Services'].includes(i.name);
        });

        // we just want the network ids for each care type
        // this will return a flat array of network ids
        return res.careTypes.flatMap((i) => {
          return i.coverageTypes.map((j) => j.networkId);
        });
      }),
      tap((res) => {
        // save filtered response in session storage
        sessionStorage.setItem(ProviderService.CareTypeNetworkIdsKey, JSON.stringify(res));
      })
    );
  }
}
